// One sampled lead as a card rather than a table row.
//
// A table is the right shape for comparing a hundred leads on one column. It is
// the wrong shape for the question this page actually asks, which is about a
// single lead: given what we know about this company, do the signals it carries
// make sense? That question is answered by reading one lead completely, and a
// row that has to keep its columns aligned with ninety-nine others cannot show a
// lead completely.
//
// So the card leads with identity and verdict, gives the signals the middle
// because they are the thing being edited, and puts the enrichment underneath as
// quiet metadata: it is the evidence, not the subject.
import React from 'react';
import { Link } from 'react-router-dom';

import { CustomerFitSampleLead, SignalTrace } from '../../../models/SampleLead';
import FitStars from './FitStars';
import { SEGMENT_LABEL } from '../trees/nodeSegment';
import { Threshold } from '../../../models/ModelPerformances';
import './leadProfileCard.css';

/**
 * The pipeline's own "no value" markers, which are not obvious from the types.
 *
 * `amountRaised` is `-333` when the company has no raised figure, and the same
 * sentinel shows up in signal text as "Company size is unknown -333".
 * `marketCap` is typed `number` but the converter writes the string `'?'` when
 * it is missing. Rendered literally, both read as facts about the company.
 */
function known(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (value === '?') return false;
  // Coerced rather than switched on `typeof`. The declared type is not the real
  // one: `marketCap` is typed `number` and arrives as a string, because the
  // converter passes that column through without a `Number()` while it casts
  // employees and raised. A numeric check guarded by `typeof value === 'number'`
  // therefore skipped it, and "$-1" stayed on every card after the check that
  // was supposed to stop it.
  const numeric = Number(value);
  if (Number.isFinite(numeric) && String(value).trim() !== '') {
    // At or below zero is a sentinel, not a measurement: -333 is the pipeline's
    // unknown for size and raised, market cap arrives as -1, and none of these
    // quantities is meaningfully zero for a company that exists.
    return numeric > 0;
  }
  return true;
}

const count = (value: number) => Number(value).toLocaleString('en-US');

/** Compact money: a raised figure is read at a glance, not audited here. */
function money(value: number): string {
  const amount = Number(value);
  if (amount >= 1e9) return `$${(amount / 1e9).toFixed(1)}B`;
  if (amount >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`;
  if (amount >= 1e3) return `$${Math.round(amount / 1e3)}k`;
  return `$${count(amount)}`;
}

/** One rendered signal: the glyph carries the polarity, the text does not. */
function SignalLine({
  text,
  trace,
  onJumpToSignal,
}: {
  text: string;
  trace?: SignalTrace;
  onJumpToSignal?: (group: number, rule: number) => void;
}) {
  const positive = text.startsWith('✔');
  const negative = text.startsWith('✘');
  const label = positive || negative ? text.slice(1).trim() : text;
  // A filled circle rather than a bare tick, which is what the score lookup page
  // uses and what makes a list of signals read as a verdict per line instead of
  // as punctuation. The glyph in the data stays the source of truth for polarity.
  const glyph = (
    <i
      className={`lpc-mark ${
        positive
          ? 'fas fa-check-circle lpc-yes'
          : negative
          ? 'fas fa-times-circle lpc-no'
          : 'far fa-circle lpc-neutral'
      }`}
      aria-label={positive ? 'positive' : negative ? 'negative' : undefined}
    />
  );

  if (!onJumpToSignal || !trace) {
    return (
      <li className="lpc-signal">
        {glyph}
        <span>{label}</span>
      </li>
    );
  }
  return (
    <li className="lpc-signal">
      {glyph}
      <button
        type="button"
        className="lpc-signal-edit"
        title="Change this signal"
        onClick={() => onJumpToSignal(trace.group, trace.rule)}
      >
        {label}
        <span className="lpc-signal-cue" aria-hidden="true">
          change
        </span>
      </button>
    </li>
  );
}

/**
 * Avatar tints, as the persons list uses them: a pastel disc per row, hue varied
 * so a long list reads as a list of people rather than a repeated icon.
 *
 * Varied by address, not by anything meaningful. The hue must not look like it
 * encodes something, which is why it is not the tier, the score or the segment.
 */
const AVATAR_TINTS = [
  { fill: '#e6f6ef', ink: '#1c6b4c' },
  { fill: '#e8f0fa', ink: '#1a68b3' },
  { fill: '#f5f2ff', ink: '#5b4bc4' },
  { fill: '#fdf3f6', ink: '#8d2f43' },
  { fill: '#fdf6e8', ink: '#8a6318' },
  { fill: '#f2fbf9', ink: '#12726a' },
];

/** Stable per address, so a lead keeps its colour across samples. */
function avatarTint(email: string) {
  const text = String(email ?? '');
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 100000;
  }
  return AVATAR_TINTS[hash % AVATAR_TINTS.length];
}

export default function LeadProfileCard({
  lead,
  tenant,
  modelId,
  showTrees,
  onJumpToSignal,
}: {
  lead: CustomerFitSampleLead;
  tenant: number | string;
  modelId: number | string;
  /**
   * Whether the leaves this lead reached mean anything. On a point based model
   * no tree scores, so the footer that carries them would be an empty strip.
   */
  showTrees: boolean;
  onJumpToSignal?: (group: number, rule: number) => void;
}) {
  const segment = String(lead.segment ?? '') as Threshold;
  const tint = avatarTint(lead.email);

  const lines = String(lead.signals ?? '')
    .replaceAll('\\n', '\n')
    .split('\n')
    .filter((line) => line.trim().length);

  // Tiles, the way the company panel in the persons view builds them: a tinted
  // icon square, the label above and the value below. Grouped by kind rather
  // than coloured per field, so the colour says "who and where" or "how big"
  // instead of pretending each field has its own identity.
  //
  // Only what is known. A card of "not available" rows says nothing about the
  // company and buries the two facts that are actually there.
  const facts: { label: string; value: string; icon: string; tone: string }[] =
    [];
  const fact = (label: string, value: string, icon: string, tone: string) =>
    facts.push({ label, value, icon, tone });
  if (known(lead.industry)) {
    fact('Industry', lead.industry, 'fas fa-briefcase', 'blue');
  }
  if (known(lead.country)) {
    fact('Country', lead.country, 'fas fa-globe', 'blue');
  }
  if (known(lead.employees)) {
    fact('Employees', count(lead.employees), 'fas fa-users', 'green');
  }
  if (known(lead.amountRaised)) {
    fact(
      'Total raised',
      money(lead.amountRaised),
      'fas fa-money-bill',
      'green'
    );
  }
  // Market cap and traffic are not here: both datapoints are being retired, so
  // the card would be teaching people to read something about to disappear.
  // They are still in the table view, which shows whatever the sample returns.

  const nodes = [lead.tree1Node, lead.tree2Node, lead.tree3Node];

  return (
    <article className="lpc">
      <header className="lpc-head">
        <span
          className="lpc-avatar"
          style={{ background: tint.fill, color: tint.ink }}
          aria-hidden="true"
        >
          <i className="fas fa-user" />
        </span>
        <span className="lpc-id">
          <span className="lpc-who" title={lead.email}>
            {lead.email}
          </span>
          <span className="lpc-sub">
            {lead.isFortune500Company && (
              <span className="lpc-flag">Fortune 500</span>
            )}
            {lead.hasEnterpriseTechnology && (
              <span className="lpc-flag">Enterprise tech</span>
            )}
          </span>
        </span>
        {/* Only when true. "Converted: no" on ninety leads is noise, and the
            interesting lead is the one that converted despite a low score. */}
        {lead.hasConverted && <span className="lpc-converted">converted</span>}
        {/* The number beside the rating it produced, rather than under the
            address where it sat on its own with nothing to relate to. */}
        <span className="lpc-verdict">
          <span className="lpc-score">{count(lead.score)}</span>
          <FitStars
            segment={segment}
            title={`${
              SEGMENT_LABEL[segment as Threshold] ?? segment
            }, score ${Math.round(Number(lead.score) || 0)}`}
          />
        </span>
      </header>

      <div className="lpc-body">
        {lines.length > 0 && (
          <ul className="lpc-signals">
            {lines.map((line, index) => (
              <SignalLine
                key={`${index}-${line}`}
                text={line}
                trace={
                  lead.signalTraces?.[index]?.text === line
                    ? lead.signalTraces[index]
                    : undefined
                }
                onJumpToSignal={onJumpToSignal}
              />
            ))}
          </ul>
        )}

        {facts.length > 0 && (
          <dl className="lpc-tiles">
            {facts.map((entry) => (
              <div className="lpc-tile" key={entry.label}>
                {/* The tinted square is this span, not the icon itself.
                    FontAwesome's own class sets a display on the element it is
                    applied to, which beats a flex display declared alongside it,
                    so the centring never took effect and every glyph sat high and
                    left inside its square. */}
                <span
                  className={`lpc-tile-icon lpc-tile-icon--${entry.tone}`}
                  aria-hidden="true"
                >
                  <i className={entry.icon} />
                </span>
                <span className="lpc-tile-text">
                  <dt>{entry.label}</dt>
                  <dd>{entry.value}</dd>
                </span>
              </div>
            ))}
          </dl>
        )}

        {showTrees && (
          <footer className="lpc-foot">
            <span className="lpc-trees">
              {nodes.map((node, index) =>
                node ? (
                  <Link
                    key={`tree_${index}`}
                    className="lpc-tree"
                    title={`Tree ${index + 1}, node ${node}`}
                    to={`/tenant/${tenant}/models/${modelId}/model/trees/${
                      index + 1
                    }/nodes/${node}`}
                  >
                    <span className="lpc-tree-n">T{index + 1}</span>#{node}
                  </Link>
                ) : null
              )}
            </span>
          </footer>
        )}
      </div>
    </article>
  );
}
